import { ArrowUpRight } from "lucide-react";
import { ASSETS } from "@/lib/site";

const ROOMS = [
  {
    id: "standard",
    name: "Standard Room",
    desc: "Twin beds, warm lamplight and a soft mountain breeze through the window.",
    img: ASSETS.room3,
    tag: "For 2 guests",
  },
  {
    id: "deluxe",
    name: "Deluxe Room",
    desc: "A larger footprint with a lounge nook, extra light and a private sit-out.",
    img: null,
    tag: "For 2 guests",
    placeholder: "Photo coming soon",
  },
  {
    id: "family",
    name: "Family Suite",
    desc: "A queen bed and a bunk for the kids — laid out for slow, unhurried mornings.",
    img: ASSETS.room2,
    tag: "For 4 guests",
  },
  {
    id: "mountain",
    name: "Mountain View Room",
    desc: "Timber-lined, floor-to-ceiling glass, and a balcony straight into the pines.",
    img: ASSETS.room1,
    tag: "For 2 guests",
  },
];

function RoomCard({ room }) {
  return (
    <article
      data-testid={`room-card-${room.id}`}
      className="sh-card rounded-3xl overflow-hidden flex flex-col group"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        {room.img ? (
          <img
            src={room.img}
            alt={room.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
          />
        ) : (
          <div className="sh-placeholder w-full h-full">
            <span>{room.placeholder}</span>
          </div>
        )}
        <div className="absolute top-4 left-4 text-[10px] uppercase tracking-widest bg-[color:var(--sh-charcoal)]/70 border border-[color:var(--sh-line)] text-[color:var(--sh-cream-dim)] px-3 py-1.5 rounded-full backdrop-blur">
          {room.tag}
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="sh-serif text-2xl text-[color:var(--sh-cream)]">{room.name}</h3>
        <p className="mt-2 text-sm text-[color:var(--sh-cream-dim)] leading-relaxed flex-1">
          {room.desc}
        </p>
        <div className="mt-6 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--sh-cream-mute)]">
              Starting from
            </div>
            <div className="sh-display text-[color:var(--sh-amber)] text-3xl mt-1">
              Coming soon
            </div>
          </div>
          <a
            href="#book"
            data-testid={`room-cta-${room.id}`}
            className="inline-flex items-center gap-1.5 text-sm text-[color:var(--sh-cream)] hover:text-[color:var(--sh-amber)] transition"
          >
            Check availability
            <ArrowUpRight size={16} strokeWidth={1.6} />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Rooms() {
  return (
    <section
      id="rooms"
      data-testid="rooms-section"
      className="relative bg-[color:var(--sh-forest-deep)] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14 sh-reveal">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-[color:var(--sh-amber)] mb-4">
              Rooms & Stays
            </div>
            <h2 className="sh-display text-[color:var(--sh-cream)] text-4xl sm:text-5xl lg:text-6xl font-medium max-w-3xl">
              Four ways to fall asleep to the mountains.
            </h2>
          </div>
          <p className="text-[color:var(--sh-cream-dim)] max-w-sm text-sm sm:text-base">
            Every room has a view, a heater and a story. Pick the one that fits your trip.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sh-reveal">
          {ROOMS.map((r) => (
            <RoomCard key={r.id} room={r} />
          ))}
        </div>
      </div>
    </section>
  );
}
